import { useState, useCallback, useMemo } from "react";
import useScanner from "../../hooks/useScanner";
import ScanModal from "../ScanModal/ScanModal";
import LotSelectorModal from "../LotSelectorModal/LotSelectorModal";
import ProductSearchModal from "../ProductSearchModal/ProductSearchModal";
import { apiCompleteWorkOrder } from "../../services/api";
import { generateTxId } from "../../utils/formatters";
import { parseGS1 } from "../../utils/gs1Parser";
import styles from "./Workflow.module.css";

export default function WorkOrderWorkflow({ products, user, onTransaction, onBack }) {
  const [step, setStep] = useState("scan_order"); // "scan_order" | "scan_items"
  const [workOrder, setWorkOrder] = useState(null);
  const [foundProduct, setFoundProduct] = useState(null);
  const [showQuantity, setShowQuantity] = useState(false);
  const [showLotSelector, setShowLotSelector] = useState(false);
  const [lotMatches, setLotMatches] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [lastAdded, setLastAdded] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showManualId, setShowManualId] = useState(false);
  const [manualId, setManualId] = useState("");
  const [error, setError] = useState("");
  const [completing, setCompleting] = useState(false);

  // Track pending stock reductions so ScanModal shows accurate available stock
  const pendingByProduct = useMemo(() => {
    const map = {};
    for (const item of itemsList) {
      map[item.productId] = (map[item.productId] || 0) + item.quantity;
    }
    return map;
  }, [itemsList]);

  const handleScanOrder = useCallback((barcode) => {
    setError("");
    setWorkOrder({ id: barcode });
    setStep("scan_items");
  }, []);

  const handleScanItem = useCallback((rawBarcode) => {
    setError("");
    setLastAdded(null);
    const gs1 = parseGS1(rawBarcode);
    const lookupBarcode = gs1.isGS1 ? gs1.gtin : rawBarcode;
    const matches = products.filter(p => (p.barcode || p.id) === lookupBarcode);
    if (matches.length === 1) {
      const product = matches[0];
      const pending = pendingByProduct[product.id] || 0;
      setFoundProduct({ ...product, stock: product.stock - pending });
      setShowQuantity(true);
    } else if (matches.length > 1) {
      setLotMatches(matches.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")));
      setShowLotSelector(true);
    } else {
      setError(`Item "${lookupBarcode}" not found in inventory.`);
    }
  }, [products, pendingByProduct]);

  const handleSearchSelect = (lots) => {
    setShowSearch(false);
    setError("");
    setLastAdded(null);
    if (lots.length === 1) {
      const product = lots[0];
      const pending = pendingByProduct[product.id] || 0;
      setFoundProduct({ ...product, stock: product.stock - pending });
      setShowQuantity(true);
    } else if (lots.length > 1) {
      setLotMatches(lots.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")));
      setShowLotSelector(true);
    }
  };

  const handleManualIdSubmit = (e) => {
    e.preventDefault();
    const id = manualId.trim();
    if (!id) return;
    setShowManualId(false);
    setManualId("");
    handleScanOrder(id);
  };

  const handleLotSelect = (product) => {
    setShowLotSelector(false);
    const pending = pendingByProduct[product.id] || 0;
    setFoundProduct({ ...product, stock: product.stock - pending });
    setShowQuantity(true);
  };

  const handleScan = step === "scan_order" ? handleScanOrder : handleScanItem;

  const { error: scanError } = useScanner({
    onScan: handleScan,
    enabled: !showQuantity && !showLotSelector && !showSearch && !showManualId,
  });

  // Just add to local list — no API call yet
  const handleConfirmUse = ({ qty, notes }) => {
    setItemsList(prev => [...prev, {
      productId: foundProduct.id,
      productName: foundProduct.description,
      manufacturer: foundProduct.manufacturer,
      lotNumber: foundProduct.lotNumber,
      quantity: qty,
      notes,
    }]);
    setLastAdded({ productName: foundProduct.description, quantity: qty });
    setShowQuantity(false);
    setFoundProduct(null);
  };

  // Just remove from local list — nothing was sent yet
  const handleRemoveItem = (index) => {
    setItemsList(prev => prev.filter((_, i) => i !== index));
  };

  // On complete: send all transactions + update work order in one go
  const handleComplete = async () => {
    if (itemsList.length === 0) {
      setError("No items to submit.");
      return;
    }
    setCompleting(true);
    try {
      // Post all transactions and update local stock
      for (const item of itemsList) {
        const product = products.find(p => p.id === item.productId);
        const currentStock = product ? product.stock : 0;
        const pending = pendingByProduct[item.productId] || 0;
        // Calculate stock after all pending items for this product
        const newStock = currentStock - pending;
        const tx = {
          id: generateTxId(),
          productId: item.productId,
          productName: item.productName,
          manufacturer: item.manufacturer,
          type: "WORK_ORDER",
          quantity: item.quantity,
          notes: item.notes,
          workOrderId: workOrder.id,
          performedBy: user.name,
          timestamp: new Date().toISOString(),
          stockAfter: newStock,
        };
        onTransaction(tx, item.productId, newStock);
      }

      // Complete work order with all items in a single call (ID in body, no URL encoding issues)
      const woItems = itemsList.map(item => ({
        productId: item.productId, productName: item.productName, quantity: item.quantity,
        lotNumber: item.lotNumber,
      }));
      await apiCompleteWorkOrder(user.token, workOrder.id, woItems);

      onBack();
    } catch (err) {
      setError(`Failed to complete work order: ${err.message}`);
      setCompleting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button onClick={onBack} className={styles.backBtn}>← Back</button>
        <div className={styles.workflowTitle}>Pharmacy Work Order</div>
      </div>

      {workOrder && (
        <div className={styles.sessionInfo}>
          <div className={styles.sessionLabel}>Work Order</div>
          <div className={styles.sessionId}>{workOrder.id}</div>
          <div className={styles.sessionMeta}>{itemsList.length} item(s) pending</div>
        </div>
      )}

      {lastAdded && (
        <div className={styles.success}>
          <div className={styles.successLabel}>Item Added</div>
          <div className={styles.successName}>{lastAdded.productName}</div>
          <div className={styles.successMeta}>×{lastAdded.quantity} — not yet submitted</div>
          <button onClick={() => setLastAdded(null)} className={styles.dismissBtn}>dismiss</button>
        </div>
      )}

      {(error || scanError) && (
        <div className={styles.error}>! {error || scanError}</div>
      )}

      <div className={styles.scannerCard}>
        <div className={styles.readyState}>
          <div className={styles.readyIcon}>{step === "scan_order" ? "📋" : "📦"}</div>
          <div className={styles.readyTitle}>
            {step === "scan_order" ? "SCAN WORK ORDER BARCODE" : "SCAN ITEM BARCODE"}
          </div>
          <div className={styles.readyHint}>
            {step === "scan_order"
              ? "Scan the barcode on the work order form"
              : "Scan items to record usage"}
          </div>
          <div className={styles.readyDivider}>or</div>
          {step === "scan_order" ? (
            showManualId ? (
              <form onSubmit={handleManualIdSubmit}>
                <input
                  value={manualId}
                  onChange={e => setManualId(e.target.value)}
                  placeholder="Enter work order ID..."
                  className={styles.manualIdInput}
                  autoFocus
                />
              </form>
            ) : (
              <button onClick={() => setShowManualId(true)} className={styles.manualSearchBtn}>
                Enter ID Manually
              </button>
            )
          ) : (
            <button onClick={() => setShowSearch(true)} className={styles.manualSearchBtn}>
              Search Manually
            </button>
          )}
        </div>
      </div>

      {step === "scan_items" && itemsList.length > 0 && (
        <div className={styles.itemsList}>
          <div className={styles.itemsHeader}>Items Pending</div>
          {itemsList.map((item, i) => (
            <div key={i} className={styles.itemRow}>
              <span className={styles.itemName}>{item.productName}</span>
              <span className={styles.itemQty}>×{item.quantity}</span>
              <button onClick={() => handleRemoveItem(i)} className={styles.removeItemBtn}>✕</button>
            </div>
          ))}
        </div>
      )}

      {step === "scan_items" && (
        <button onClick={handleComplete} disabled={completing} className={styles.completeBtn}>
          {completing ? "SUBMITTING..." : `SUBMIT WORK ORDER (${itemsList.length} items)`}
        </button>
      )}

      {showLotSelector && (
        <LotSelectorModal lots={lotMatches} mode="workOrder" onSelect={handleLotSelect}
          onClose={() => { setShowLotSelector(false); setLotMatches([]); }} />
      )}
      {showQuantity && foundProduct && (
        <ScanModal product={foundProduct} mode="workOrder" onConfirm={handleConfirmUse}
          onClose={() => { setShowQuantity(false); setFoundProduct(null); }} />
      )}
      {showSearch && (
        <ProductSearchModal products={products} mode="workOrder" onSelect={handleSearchSelect}
          onClose={() => setShowSearch(false)} />
      )}
    </div>
  );
}
